import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api.js';
import { formatDay } from '../format.js';
import { PasswordInput } from './PasswordInput.js';

interface Feed {
  id: string;
  name: string;
  host: string;
  lastFetchedAt: string | null;
  lastError: string | null;
  events: number;
}

interface Subscription {
  active: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export const FEEDS_KEY = ['calendar-feeds'] as const;
const SUBSCRIPTION_KEY = ['calendar-subscription'] as const;

/**
 * Kalender verbinden — in beide Richtungen, aber ohne Zwei-Wege-Abgleich
 * (Entscheidung 13.10.2026): Apple/Google lesen Kraftwerk über eine
 * Abo-Adresse, Kraftwerk liest Apple/Google über deren geheimen Abo-Link.
 */
export function CalendarSetting(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="settings-calendar" className="kw-tile flex flex-col gap-3">
      <h3 id="settings-calendar" className="kw-kicker">
        {t('calendar.settings.heading')}
      </h3>
      <FeedList />
      <SubscriptionPart />
    </section>
  );
}

function FeedList(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const nameId = useId();
  const urlId = useId();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  const list = useQuery({
    queryKey: FEEDS_KEY,
    queryFn: async () => (await apiFetch<{ feeds: Feed[] }>('/calendar/feeds')).feeds,
  });
  const add = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; events: number }>('/calendar/feeds', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() || t('calendar.settings.defaultName'), url }),
      }),
    onSuccess: async () => {
      setAdding(false);
      setName('');
      setUrl('');
      await queryClient.invalidateQueries({ queryKey: FEEDS_KEY });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<undefined>(`/calendar/feeds/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FEEDS_KEY }),
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold">{t('calendar.settings.importHeading')}</p>
      <p className="text-xs text-ink-muted">{t('calendar.settings.importIntro')}</p>

      {list.isError ? (
        <p className="text-xs text-ink-muted">{t('passkeys.offline')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {(list.data ?? []).map((feed) => (
            <li
              key={feed.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-line px-3 py-2 text-sm"
            >
              <span className="min-w-0">
                <span className="block truncate font-semibold">{feed.name}</span>
                <span className="block truncate text-xs text-ink-muted">
                  {feed.host} · {t('calendar.settings.events', { count: feed.events })}
                  {feed.lastFetchedAt
                    ? ` · ${t('calendar.settings.fetched', {
                        date: formatDay(feed.lastFetchedAt.slice(0, 10), 'date', i18n.language),
                      })}`
                    : ''}
                </span>
                {feed.lastError ? (
                  <span className="block text-xs text-warn">
                    {t('calendar.settings.failed', { reason: feed.lastError })}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => remove.mutate(feed.id)}
                aria-label={t('calendar.settings.removeNamed', { name: feed.name })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
              >
                {t('calendar.settings.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <label htmlFor={nameId} className="flex flex-col gap-1">
            <span className="kw-label">{t('calendar.settings.name')}</span>
            <input
              id={nameId}
              value={name}
              maxLength={60}
              onChange={(event) => setName(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={urlId} className="flex flex-col gap-1">
            <span className="kw-label">{t('calendar.settings.url')}</span>
            <input
              id={urlId}
              type="text"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={url}
              placeholder="https://"
              onChange={(event) => setUrl(event.target.value)}
              className="kw-field"
              required
            />
          </label>
          <details className="text-xs text-ink-muted">
            <summary className="cursor-pointer">{t('calendar.settings.howTo')}</summary>
            <p className="mt-1">{t('calendar.settings.howToApple')}</p>
            <p className="mt-1">{t('calendar.settings.howToGoogle')}</p>
          </details>
          {add.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t('calendar.settings.addFailed', { reason: (add.error as Error).message })}
            </p>
          ) : null}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={add.isPending || url.trim() === ''}
              className="kw-btn kw-btn-small flex-1"
            >
              {add.isPending ? t('calendar.settings.adding') : t('calendar.settings.add')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('nutrition.library.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('calendar.settings.addFeed')}
        </button>
      )}
    </div>
  );
}

function SubscriptionPart(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const passwordId = useId();
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState('');
  const [fresh, setFresh] = useState<{ url: string; webcal: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const status = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: () => apiFetch<Subscription>('/calendar/subscription'),
  });
  const create = useMutation({
    mutationFn: () =>
      apiFetch<{ url: string; webcal: string }>('/calendar/subscription', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
    onSuccess: async (result) => {
      setFresh(result);
      setAsking(false);
      setPassword('');
      await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });
  const revoke = useMutation({
    mutationFn: () => apiFetch<undefined>('/calendar/subscription', { method: 'DELETE' }),
    onSuccess: async () => {
      setFresh(null);
      await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-3">
      <p className="text-sm font-semibold">{t('calendar.settings.exportHeading')}</p>
      <p className="text-xs text-ink-muted">{t('calendar.settings.exportIntro')}</p>

      {fresh ? (
        <div role="status" className="flex flex-col gap-2 rounded-xl border border-good p-3">
          <p className="text-sm font-semibold">{t('calendar.settings.onlyOnce')}</p>
          <a href={fresh.webcal} className="kw-btn kw-btn-small">
            {t('calendar.settings.openApple')}
          </a>
          <code className="block break-all rounded-lg bg-surface-raised p-2 text-xs">
            {fresh.url}
          </code>
          <button
            type="button"
            onClick={() =>
              void navigator.clipboard?.writeText(fresh.url).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {copied ? t('shortcuts.copied') : t('calendar.settings.copy')}
          </button>
          <p className="text-xs text-ink-muted">{t('calendar.settings.googleHint')}</p>
        </div>
      ) : null}

      {status.data?.active && !fresh ? (
        <p className="text-sm">{t('calendar.settings.active')}</p>
      ) : null}

      {asking ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <label htmlFor={passwordId} className="flex flex-col gap-1">
            <span className="kw-label">{t('calendar.settings.password')}</span>
            <PasswordInput
              id={passwordId}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          {create.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t('calendar.settings.passwordWrong')}
            </p>
          ) : null}
          <button type="submit" disabled={password === ''} className="kw-btn kw-btn-small">
            {t('calendar.settings.create')}
          </button>
        </form>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAsking(true)}
            className="kw-btn kw-btn-small flex-1"
          >
            {status.data?.active ? t('calendar.settings.renew') : t('calendar.settings.create')}
          </button>
          {status.data?.active ? (
            <button
              type="button"
              onClick={() => revoke.mutate()}
              className="kw-btn kw-btn-quiet kw-btn-small flex-1"
            >
              {t('calendar.settings.revoke')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
